import { subscribeToAdFrequencyEvents } from './EventBus';
import { createLogger, Logger, LoggerLevel } from '@quickplay/foundation';
import {
  evaluateSession,
  normalizeConfiguration,
  SessionState,
} from './policy';
import {
  AdFrequencyControlConfiguration,
  AdFrequencyController,
  AdFrequencyEvent,
  AdFrequencyStateStorage,
} from './types';

type ContentConsumptionSyncHandler = () => void;

let activeContentConsumptionSyncHandler: ContentConsumptionSyncHandler | undefined;

/**
 * Registers the active playback tracker's synchronous content synchronization operation.
 * The returned function unregisters the handler only if it is still active.
 */
export function registerActiveContentConsumptionSync(handler: ContentConsumptionSyncHandler): () => void {
  activeContentConsumptionSyncHandler = handler;
  return () => {
    if (activeContentConsumptionSyncHandler === handler) {
      activeContentConsumptionSyncHandler = undefined;
    }
  };
}

/** Singleton AFC policy controller shared by the active playback session. */
export class AdFrequencyControllerImpl implements AdFrequencyController {
  private static readonly STORAGE_KEY = 'quickplay.ad-frequency-control.v1';
  private static instance: AdFrequencyControllerImpl | undefined;
  private readonly session: SessionState;
  private breakQualified = false;
  private logger: Logger = createLogger(LoggerLevel.OFF, 'AFC');
  private readonly storage?: AdFrequencyStateStorage;

  constructor() {
    this.session = {
      configuration: normalizeConfiguration({}),
      consumed: 0,
      hasEvaluatedOpportunity: false,
      hasQualifyingExposure: false,
    };
    this.storage = this.getDefaultStorage();
    this.restoreState();
    subscribeToAdFrequencyEvents(event => this.apply(event));
  }

  /**
   * Returns the shared controller instance and updates its active policy when
   * a later playback supplies new configuration.
   */
  static getInstance(): AdFrequencyControllerImpl {
    AdFrequencyControllerImpl.instance ??= new AdFrequencyControllerImpl();
    return AdFrequencyControllerImpl.instance;
  }

  /**
   * Evaluates the current opportunity after synchronizing the active playback
   * tracker, including when the application calls the controller directly.
   */
  evaluateOpportunity(): boolean {
    try {
      activeContentConsumptionSyncHandler?.();
    } catch (error) {
      this.logger.log('[AFC] Failed to synchronize content consumption before opportunity evaluation', { error });
    }

    try {
      const decision = evaluateSession(this.session);
      this.session.hasEvaluatedOpportunity = true;
      this.persistState();
      this.logger.log('Opportunity evaluated', decision);
      return decision.allowed;
    } catch {
      this.logger.log('Opportunity evaluation failed; allowing opportunity');
      return true;
    }
  }

  /**
   * @param loggerLevel The desired logging level for the AFC controller.
   */
  setLoggerLevel(loggerLevel: LoggerLevel): void {
    this.logger = createLogger(loggerLevel, 'AFC');
  }

  /**
   * Resets the controller to its initial state, clearing all accumulated content consumption and ad break exposure flags.
   */

  reset(): void {
    this.logger.log('Controller reset');
    this.session.consumed = 0;
    this.session.hasEvaluatedOpportunity = false;
    this.session.hasQualifyingExposure = false;
    this.breakQualified = false;
    this.persistState();
  }

  /** Applies policy values for the active playback session. */
  /** @internal Configured by AFCPlaybackSessionManager, not application code. */
  configure(configuration: AdFrequencyControlConfiguration): void {
    this.session.configuration = normalizeConfiguration(configuration);
    this.logger.log('Controller configuration updated', {
      minimumContentConsumptionInMS: this.session.configuration.minimumContentConsumptionInMS,
      adBreakExposureThreshold: this.session.configuration.adBreakExposureThreshold,
      allowFirstOpportunity: this.session.configuration.allowFirstOpportunity,
    });
  }

  private apply(event: AdFrequencyEvent): void {
    switch (event.type) {
      case 'session-started':
        this.logger.log('Session started');
        return;
      case 'content-consumed':
        if (Number.isFinite(event.duration) && event.duration > 0) {
          this.session.consumed += event.duration;
          this.persistState();
          this.logger.log('Content consumption accumulated', {
            duration: event.duration,
            consumed: this.session.consumed,
          });
        }
        return;
      case 'ad-break-progressed':
        if (event.progress.watched === 0) {
          this.breakQualified = false;
          this.logger.log('Ad break progress reset', event.progress);
          return;
        }
        if (
          !this.breakQualified &&
          ((event.progress.totalDuration !== undefined &&
            event.progress.totalDuration > 0 &&
            event.progress.watched / event.progress.totalDuration >=
            this.session.configuration.adBreakExposureThreshold) ||
            (event.progress.isComplete === true && event.progress.totalDuration === undefined))
        ) {
          this.breakQualified = true;
          this.session.hasQualifyingExposure = true;
          this.session.consumed = 0;
          this.persistState();
          this.logger.log('Qualifying ad exposure reached; content consumption reset', event.progress);
        }
        return;
      case 'session-ended':
        this.persistState();
        this.logger.log('Session ended', {
          consumed: this.session.consumed,
          hasQualifyingExposure: this.session.hasQualifyingExposure,
        });
        return;
    }
  }

  private getDefaultStorage(): AdFrequencyStateStorage | undefined {
    if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) return undefined;
    const localStorage = globalThis.localStorage;
    if (!localStorage) return undefined;
    return {
      get: key => localStorage.getItem(key) ?? undefined,
      set: (key, value) => localStorage.setItem(key, value),
    };
  }

  private restoreState(): void {
    if (!this.storage) return;
    try {
      const raw = this.storage.get(AdFrequencyControllerImpl.STORAGE_KEY);
      if (raw) {
        const persisted = JSON.parse(raw) as {
          consumed?: number;
          hasEvaluatedOpportunity?: boolean;
          hasQualifyingExposure?: boolean;
        };
        const consumed = persisted.consumed;
        if (typeof consumed === 'number' && Number.isFinite(consumed) && consumed >= 0) {
          this.session.consumed = consumed;
        }
        if (typeof persisted.hasEvaluatedOpportunity === 'boolean') {
          this.session.hasEvaluatedOpportunity = persisted.hasEvaluatedOpportunity;
        }
        if (typeof persisted.hasQualifyingExposure === 'boolean') {
          this.session.hasQualifyingExposure = persisted.hasQualifyingExposure;
        }
        this.logger.log('Restored persisted state', {
          consumed: this.session.consumed,
          hasEvaluatedOpportunity: this.session.hasEvaluatedOpportunity,
          hasQualifyingExposure: this.session.hasQualifyingExposure,
        });
      }
      this.persistState();
    } catch (error) {
      this.logger.log('Failed to restore persisted state', { error });
      this.persistState();
    }
  }

  private persistState(): void {
    if (!this.storage) return;
    try {
      this.storage.set(
        AdFrequencyControllerImpl.STORAGE_KEY,
        JSON.stringify({
          consumed: this.session.consumed,
          hasEvaluatedOpportunity: this.session.hasEvaluatedOpportunity,
          hasQualifyingExposure: this.session.hasQualifyingExposure,
        }),
      );
    } catch (error) {
      this.logger.log('Failed to persist state', { error });
    }
  }
}
